import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { BRANCHES, DR_BLACK, LANE_IDEA_COLOR, LANE_CONCERN_COLOR } from '../pages/speakup/speakupConstants';
import { speakUpOrigin, flyerUrlForBranch } from './speakupHost';
import logoUrl from '../assets/dr-signs-logo.png';

// ---------------------------------------------------------------------------
// Speak Up wall flyers. Issue #57, closed at OOB-5.
//
// #57's finding, verified by a full-repo grep at the July audit: the four
// branch flyers "shipped at Session 43" exist nowhere in source. They were a
// one-off produced outside the app, and its Owner line says outright that
// "whoever manages the getstede.com domain migration needs to know the flyers
// can't be regenerated from source; they'll have to be manually recreated with
// the new domain."
//
// THAT SENTENCE IS WHY THIS FILE EXISTS RATHER THAN A DESIGNER'S PDF. The
// domain is changing again — to speakup.team — and a flyer that cannot
// be regenerated has to be remade by hand every time it does. The URL on the
// wall is the one thing here that MUST be correct, and a hand-made artifact is
// the one thing nobody re-checks.
//
// So the address comes from speakUpOrigin(), the same function the app routes
// on. The flyer cannot disagree with the application about where Speak Up
// lives, because they read the same constant.
//
// US LETTER, NOT A5, AND THAT WAS A REAL DEFECT. This file first specified
// [420, 595] — A5, 148 × 210 mm. D&R prints on an office printer, which is
// 8.5 × 11 in / 612 × 792 pt, so every flyer would have come out scaled or
// cropped depending on the driver. Colby caught it from the mockup's
// proportions. Letter is also a SQUARER page than A5, so the fix was not a
// rescale: the code now sits beside its instruction and the two lanes run side
// by side, because there is width to use.
//
// NOTHING BLEEDS TO THE EDGE. Consumer printers cannot print borderless, so a
// full-width black bar would come back framed in white and shifted. Every
// element sits inside a 48 pt margin.
// ---------------------------------------------------------------------------

const PAGE = { w: 612, h: 792 };
const M = 48;                       // margin, 2/3 inch
const CONTENT_W = PAGE.w - M * 2;   // 516

// The logo ships as a transparent PNG — verified at OOB-5 by decoding it:
// all four corners are alpha 0, so it drops onto the black bar with no white
// square behind it. jsPDF needs a data URL rather than a bundler URL, so it is
// fetched once and cached for the life of the page.
let logoDataUrl = null;
async function loadLogo() {
  if (logoDataUrl) return logoDataUrl;
  try {
    const res = await fetch(logoUrl);
    const blob = await res.blob();
    logoDataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
    return logoDataUrl;
  } catch (e) {
    // A missing logo must not cost the flyer. The address is the load-bearing
    // content; the mark is identification.
    return null;
  }
}

export function flyerUrls() {
  return BRANCHES.map((b) => ({ id: b.id, name: b.name, slug: b.slug, url: flyerUrlForBranch(b.slug) }));
}

function drawFlyer(doc, branch, qr, logo) {
  const cx = PAGE.w / 2;

  // EVERYTHING ON THIS SHEET IS SIZED FOR A WALL, NOT A DESK. The first pass
  // used a 44 pt logo and 13 pt brand text — 0.6 in and 0.18 in on an 8.5 x 11
  // sheet, which is business-card scale. Colby's note: "everything seems too
  // small." A flyer is read standing up, at a distance, in a shop.

  // --- brand bar -----------------------------------------------------------
  doc.setFillColor(DR_BLACK);
  doc.rect(M, M, CONTENT_W, 96, 'F');
  if (logo) doc.addImage(logo, 'PNG', M + 18, M + 12, 72, 72, undefined, 'FAST');
  doc.setTextColor('#FFFFFF');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(23);
  doc.text('D&R SIGNS', M + 106, M + 58);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor('#9A9A94');
  doc.text('EST. 2002', M + 106, M + 76);

  // --- headline ------------------------------------------------------------
  doc.setTextColor(DR_BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(58);
  doc.text('Speak Up', cx, 232, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(15);
  doc.setTextColor('#6B6A65');
  doc.text('Anonymous, always. Nothing records who you are.', cx, 262, { align: 'center' });

  // --- scan block: the code beside its instruction -------------------------
  const qrSize = 190;
  const qrX = M + 26;
  const qrY = 300;
  doc.setDrawColor(DR_BLACK);
  doc.setLineWidth(2);
  doc.rect(qrX - 13, qrY - 13, qrSize + 26, qrSize + 26, 'S');
  // 'FAST' compression: jsPDF stores images uncompressed by default, which
  // made one page 351 KB and the four-branch set 1.26 MB.
  doc.addImage(qr, 'PNG', qrX, qrY, qrSize, qrSize, undefined, 'FAST');

  const tx = qrX + qrSize + 48;
  doc.setTextColor(DR_BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('Point your phone', tx, qrY + 48);
  doc.text('camera at the code.', tx, qrY + 72);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13.5);
  doc.setTextColor('#7A7974');
  doc.text('No app. No login.', tx, qrY + 108);
  doc.text('No account.', tx, qrY + 127);
  doc.setDrawColor('#DEDDD8');
  doc.setLineWidth(0.7);
  doc.line(tx, qrY + 148, M + CONTENT_W - 26, qrY + 148);
  doc.setFontSize(13);
  doc.setTextColor('#2E2D2A');
  doc.text(speakUpOrigin().replace(/^https?:\/\//, ''), tx, qrY + 170);

  // --- branch band ---------------------------------------------------------
  // The one element that differs between the four sheets, and the reason it is
  // a coloured band rather than a word: a flyer pinned in the wrong shop
  // should be a glance, not a read.
  const bandY = 546;
  doc.setFillColor('#F2F1EC');
  doc.rect(M, bandY, CONTENT_W, 62, 'F');
  doc.setFillColor(branch.color);
  doc.rect(M, bandY, CONTENT_W, 6, 'F');
  doc.setTextColor(DR_BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(31);
  doc.text(branch.name, M + 20, bandY + 44);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor('#7A7974');
  doc.text('BRANCH', M + CONTENT_W - 20, bandY + 44, { align: 'right' });

  // --- the two lanes, side by side ----------------------------------------
  // Their colours are the app's own lane colours, so the wall and the screen
  // agree: green ideas, purple concerns.
  const laneY = bandY + 106;
  const colW = (CONTENT_W - 30) / 2;
  const lanes = [
    { x: M, color: LANE_IDEA_COLOR, title: 'Share an idea',
      body: [`Your ${branch.name} branch manager`, 'reads it, as you wrote it.'] },
    { x: M + colW + 30, color: LANE_CONCERN_COLOR, title: 'Raise a concern',
      body: ['Goes to ownership, read', 'personally, and nobody else.'] },
  ];
  lanes.forEach((lane) => {
    doc.setFillColor(lane.color);
    doc.rect(lane.x, laneY - 17, 5, 62, 'F');
    doc.setTextColor(DR_BLACK);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16.5);
    doc.text(lane.title, lane.x + 17, laneY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12.5);
    doc.setTextColor('#7A7974');
    lane.body.forEach((line, i) => doc.text(line, lane.x + 17, laneY + 21 + i * 17));
  });

  // --- what happens next ---------------------------------------------------
  doc.setDrawColor('#DEDDD8');
  doc.setLineWidth(0.7);
  doc.line(M, 712, M + CONTENT_W, 712);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12.5);
  doc.setTextColor('#6B6A65');
  doc.text("You'll get a private link to keep — check it any time for a reply.", cx, 736, { align: 'center' });
}

export async function buildSpeakUpFlyers(branchIds = null) {
  const wanted = branchIds && branchIds.length
    ? BRANCHES.filter((b) => branchIds.includes(b.id))
    : BRANCHES;
  if (!wanted.length) return null;

  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });
  const logo = await loadLogo();

  for (let i = 0; i < wanted.length; i += 1) {
    if (i > 0) doc.addPage('letter', 'portrait');
    const branch = wanted[i];
    // Error correction H: these get pinned to a shop wall and will be scuffed,
    // taped at a corner, and photographed at an angle.
    const qr = await QRCode.toDataURL(flyerUrlForBranch(branch.slug), {
      // 320 px placed at 190 pt is ~1.7x the printed size — past what any
      // printer resolves, and a quarter of the file. The first pass used 600,
      // which made ONE page 1.13 MB and a four-branch set ~4.5 MB. A print job
      // that large is a real cost on an office printer over wifi.
      errorCorrectionLevel: 'H', margin: 0, width: 320,
    });
    drawFlyer(doc, branch, qr, logo);
  }

  return doc.output('blob');
}
